import { UserPlus, FileSearch, Coins, Rocket } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useLanguage } from "../../contexts/LanguageContext";

const steps = [
  {
    icon: UserPlus,
    step: "01",
    title: "التسجيل",
    description: "أنشئ حسابك كمستثمر أو صاحب مشروع وأكمل ملفك الشخصي",
  },
  {
    icon: FileSearch,
    step: "02",
    title: "التقييم",
    description: "يقوم خبراؤنا بتقييم المشاريع وتحليل فرص الاستثمار",
  },
  {
    icon: Coins,
    step: "03",
    title: "التمويل",
    description: "نربط المستثمرين بالمشاريع المناسبة لتحقيق الأهداف المشتركة",
  },
  {
    icon: Rocket,
    step: "04",
    title: "التنفيذ",
    description: "نتابع المشروع خطوة بخطوة حتى تحقيق النجاح",
  },
];

export function HowItWorksSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const { t, language } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Define steps with translation
  const translatedSteps = [
    {
      icon: UserPlus,
      step: "01",
      title: t('public.howItWorks.step1.title'),
      description: t('public.howItWorks.step1.description'),
    },
    {
      icon: FileSearch,
      step: "02",
      title: t('public.howItWorks.step2.title'),
      description: t('public.howItWorks.step2.description'),
    },
    {
      icon: Coins,
      step: "03",
      title: t('public.howItWorks.step3.title'),
      description: t('public.howItWorks.step3.description'),
    },
    {
      icon: Rocket,
      step: "04",
      title: t('public.howItWorks.step4.title'),
      description: t('public.howItWorks.step4.description'),
    },
  ];

  return (
    <section ref={sectionRef} className="py-12 sm:py-16 md:py-20 bg-muted/30 overflow-hidden" dir={language === "ar" ? "rtl" : "ltr"}>
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className={`text-center max-w-2xl mx-auto mb-12 sm:mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <span className="text-sm font-semibold text-primary uppercase tracking-wide">{t('public.howItWorks.title')}</span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mt-3 mb-4">
            {t('public.howItWorks.subtitle')}
          </h2>
          <p className="text-muted-foreground text-sm sm:text-base">
            {t('public.howItWorks.description')}
          </p>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
          {translatedSteps.map((step, index) => (
            <div
              key={step.step}
              className={`relative transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              {/* Connector Line */}
              {index < translatedSteps.length - 1 && (
                <div className={`hidden md:block absolute top-16 left-0 w-full h-0.5 bg-border -translate-x-1/2 transition-all duration-1000 ${isVisible ? 'scale-x-100' : 'scale-x-0'}`} style={{ transitionDelay: `${index * 200 + 500}ms` }} />
              )}

              <div className="relative bg-card rounded-xl sm:rounded-2xl p-6 sm:p-8 shadow-card hover-lift group">
                {/* Step Number */}
                <span className="absolute -top-3 right-4 text-4xl font-bold text-muted/50 group-hover:text-primary/20 transition-colors duration-300">
                  {step.step}
                </span>

                {/* Icon */}
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg sm:rounded-xl bg-hero-gradient flex items-center justify-center mb-4 sm:mb-6 group-hover:scale-110 transition-transform duration-300">
                  <step.icon className="w-6 h-6 sm:w-8 sm:h-8 text-primary-foreground" />
                </div>

                {/* Content */}
                <h3 className="text-lg sm:text-xl font-bold mb-2 sm:mb-3">{step.title}</h3>
                <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">{step.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}